import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  LinearProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon,
  TrendingUp as ProgressIcon,
  Flag as FlagIcon,
  Comment as CommentIcon,
  AttachFile as AttachIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const GoalManagement = () => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    employeeId: '',
    status: '',
    category: '',
    priority: '',
    page: 1,
    limit: 20
  });
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [progressDialog, setProgressDialog] = useState(false);
  const [progressData, setProgressData] = useState({ progress: 0, comment: '' });

  useEffect(() => {
    fetchGoals();
    fetchStats();
  }, [filters]);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/employee-goal/list', filters);
      if (response.data.success) {
        setGoals(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/employee-goal/stats/overview');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleView = async (id) => {
    try {
      const response = await axios.get(`/api/employee-goal/${id}`);
      if (response.data.success) {
        setSelectedGoal(response.data.data);
        setViewDialog(true);
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch goal details', 'error');
    }
  };

  const handleUpdateProgress = async () => {
    try {
      const response = await axios.post(`/api/employee-goal/progress/${selectedGoal._id}`, progressData);
      if (response.data.success) {
        Swal.fire('Success', 'Progress updated successfully', 'success');
        setProgressDialog(false);
        fetchGoals();
        fetchStats();
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to update progress', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Goal?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`/api/employee-goal/${id}`);
        if (response.data.success) {
          Swal.fire('Deleted', 'Goal deleted successfully', 'success');
          fetchGoals();
          fetchStats();
        }
      } catch (error) {
        Swal.fire('Error', 'Failed to delete goal', 'error');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      NOT_STARTED: 'default',
      IN_PROGRESS: 'primary',
      COMPLETED: 'success',
      OVERDUE: 'error',
      ON_HOLD: 'warning',
      CANCELLED: 'default'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      LOW: 'success',
      MEDIUM: 'info',
      HIGH: 'warning',
      CRITICAL: 'error'
    };
    return colors[priority] || 'default';
  };

  const getDaysRemaining = (targetDate, status) => {
    if (status === 'COMPLETED' || status === 'CANCELLED') return 'N/A';
    const today = new Date();
    const target = new Date(targetDate);
    const diffTime = target - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Goal Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/userdashboard/staff/hrm/performance/goals/create')}
        >
          Create New Goal
        </Button>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Total Goals</Typography>
                <Typography variant="h4">{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Not Started</Typography>
                <Typography variant="h4" color="text.secondary">{stats.notStarted}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">In Progress</Typography>
                <Typography variant="h4" color="primary.main">{stats.inProgress}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Completed</Typography>
                <Typography variant="h4" color="success.main">{stats.completed}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Overdue</Typography>
                <Typography variant="h4" color="error.main">{stats.overdue}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">Cancelled</Typography>
                <Typography variant="h4">{stats.cancelled}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="NOT_STARTED">Not Started</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="OVERDUE">Overdue</MenuItem>
              <MenuItem value="ON_HOLD">On Hold</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Category"
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="PERFORMANCE">Performance</MenuItem>
              <MenuItem value="SKILL_DEVELOPMENT">Skill Development</MenuItem>
              <MenuItem value="PROJECT">Project</MenuItem>
              <MenuItem value="SALES">Sales</MenuItem>
              <MenuItem value="QUALITY">Quality</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              select
              label="Priority"
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Priorities</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Goals Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Goal Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Target Date</TableCell>
                <TableCell>Days Remaining</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">Loading...</TableCell>
                </TableRow>
              ) : goals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">No goals found</TableCell>
                </TableRow>
              ) : (
                goals.map((goal) => (
                  <TableRow key={goal._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={goal.employeeId?.imageUrl} sx={{ width: 32, height: 32 }}>
                          {goal.employeeId?.staff_name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {goal.employeeId?.staff_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {goal.employeeId?.emp_id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{goal.goalTitle}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {goal.goalType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={goal.category} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={goal.priority} 
                        color={getPriorityColor(goal.priority)} 
                        size="small"
                        icon={<FlagIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={goal.status.replace('_', ' ')} 
                        color={getStatusColor(goal.status)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={goal.progress} 
                          sx={{ width: 80, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption">{goal.progress}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(goal.targetDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={getDaysRemaining(goal.targetDate, goal.status) === 'Overdue' ? 'error' : 'text.primary'}
                      >
                        {getDaysRemaining(goal.targetDate, goal.status)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(goal._id)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Progress">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setSelectedGoal(goal);
                              setProgressData({ progress: goal.progress, comment: '' });
                              setProgressDialog(true);
                            }}
                          >
                            <ProgressIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/userdashboard/staff/hrm/performance/goals/edit/${goal._id}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(goal._id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Goal Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Goal Details</DialogTitle>
        <DialogContent>
          {selectedGoal && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6">{selectedGoal.goalTitle}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedGoal.description}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Category</Typography>
                  <Typography variant="body1">{selectedGoal.category}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Priority</Typography>
                  <Typography variant="body1">{selectedGoal.priority}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography variant="body1">
                    {new Date(selectedGoal.startDate).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Target Date</Typography>
                  <Typography variant="body1">
                    {new Date(selectedGoal.targetDate).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Progress</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={selectedGoal.progress} 
                      sx={{ flex: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="h6">{selectedGoal.progress}%</Typography>
                  </Box>
                </Grid>

                {selectedGoal.milestones && selectedGoal.milestones.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Milestones
                    </Typography>
                    <List>
                      {selectedGoal.milestones.map((milestone, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {milestone.completed ? (
                                  <CompleteIcon color="success" fontSize="small" />
                                ) : (
                                  <Box sx={{ width: 20, height: 20, border: '2px solid #ccc', borderRadius: '50%' }} />
                                )}
                                <Typography variant="body2">{milestone.title}</Typography>
                              </Box>
                            }
                            secondary={
                              milestone.targetDate 
                                ? `Target: ${new Date(milestone.targetDate).toLocaleDateString()}`
                                : null
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {selectedGoal.comments && selectedGoal.comments.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Comments
                    </Typography>
                    <List>
                      {selectedGoal.comments.map((comment, index) => (
                        <React.Fragment key={index}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={comment.commentByName}
                              secondary={
                                <>
                                  <Typography variant="body2" color="text.primary">
                                    {comment.comment}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(comment.commentDate).toLocaleString()}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                          {index < selectedGoal.comments.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update Progress Dialog */}
      <Dialog open={progressDialog} onClose={() => setProgressDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Progress</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Progress (%)"
              value={progressData.progress}
              onChange={(e) => setProgressData({ ...progressData, progress: Math.min(100, Math.max(0, e.target.value)) })}
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comment (Optional)"
              value={progressData.comment}
              onChange={(e) => setProgressData({ ...progressData, comment: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateProgress} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoalManagement;
